import { useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { errorMessage, formatDateTime } from '../members/hooks';
import type { VaultResult } from './types';

const MIN_REASON = 10;

/**
 * Lectura motivada de la bóveda de IP (R6.5). El resultado vive solo en memoria de este componente:
 * no se guarda en el navegador y se borra al salir o con "Limpiar".
 */
export function IpVaultPanel() {
  const [code, setCode] = useState('');
  const [domain, setDomain] = useState('q-r.cl');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VaultResult | null>(null);

  const reasonError = reason.trim().replace(/\s+/g, ' ').length < MIN_REASON ? `Indica el motivo de la lectura (al menos ${MIN_REASON} caracteres).` : undefined;
  const codeError = !/^[A-Za-z0-9]{1,12}$/.test(code.trim()) ? 'Escribe el código del QR.' : undefined;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (reasonError || codeError) return;
    setLoading(true);
    try {
      const body: Record<string, string> = { code: code.trim().toUpperCase(), domain: domain.trim(), reason: reason.trim() };
      if (from) body.from = new Date(`${from}T00:00:00`).toISOString();
      if (to) body.to = new Date(`${to}T23:59:59.999`).toISOString();
      setResult(await api.post<VaultResult>('/v1/operator/ip-vault/read', body));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-line bg-white p-4" aria-labelledby="boveda-titulo">
      <h2 id="boveda-titulo" className="font-display text-base font-bold text-ink">Bóveda de IP de visitantes</h2>
      <Banner tone="warning">Cada lectura queda en la auditoría con tu cuenta, los hits leídos y el motivo. Úsala solo para investigar abuso o por requerimiento legal.</Banner>
      <form onSubmit={onSubmit} className="space-y-3" noValidate autoComplete="off">
        <div className="grid grid-cols-[1fr_8rem] gap-3">
          <Input label="Código" mono value={code} onChange={(e) => setCode(e.target.value)} error={touched ? codeError : undefined} />
          <Input label="Dominio" value={domain} onChange={(e) => setDomain(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Desde" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label="Hasta" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Input label="Motivo de la lectura" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} error={touched ? reasonError : undefined} />
        <div className="flex justify-end gap-2">
          {result && <Button variant="secondary" onClick={() => setResult(null)}>Limpiar resultados</Button>}
          <Button type="submit" loading={loading}>Leer IPs</Button>
        </div>
      </form>
      <div aria-live="polite">
        {error && <Banner tone="error">{error}</Banner>}
        {result && (
          <div className="space-y-2">
            <p className="text-sm text-ink">
              {result.count === 0 ? 'No hay IPs vigentes para ese QR y rango (se conservan 90 días).' : `${result.count} IP${result.count === 1 ? '' : 's'} leída${result.count === 1 ? '' : 's'}.`}
              {result.missing.length > 0 && <span className="text-ink-muted"> {result.missing.length} sin dato (vencidas o ilegibles).</span>}
            </p>
            {result.items.length > 0 && (
              <div className="max-h-72 overflow-auto rounded-md border border-line">
                <table className="w-full text-sm">
                  <caption className="sr-only">IPs leídas de la bóveda</caption>
                  <thead className="text-xs text-ink-muted">
                    <tr>
                      <th scope="col" className="px-3 py-1 text-left font-medium">Fecha</th>
                      <th scope="col" className="px-3 py-1 text-left font-medium">IP</th>
                      <th scope="col" className="px-3 py-1 text-left font-medium">Hit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.items.map((i) => (
                      <tr key={i.hitId} className="border-t border-line">
                        <td className="px-3 py-1 tabular-nums">{formatDateTime(i.createdAt)}</td>
                        <td className="px-3 py-1 font-mono">{i.ip}</td>
                        <td className="px-3 py-1 font-mono text-xs text-ink-muted">{i.hitId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
